import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { utc } from "./syncText";

const stepUp = async (code) => (await api.post("/auth/broker/funds-audit-step-up", { totp_code: code })).data.step_up_token;

function CodeInput({ value, onChange, testId }) {
  return <Input data-testid={testId} inputMode="numeric" maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="h-7 w-28 bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-xs" />;
}

function AuditRow({ r, edit, onEdit }) {
  const v = { ...r, ...edit };
  const set = (k) => (e) => onEdit(r.name, { ...edit, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const cell = "px-2 py-1 border-t border-[var(--border-subtle)]";
  return (
    <tr data-testid={`funds-audit-row-${r.name}`}>
      <td className={`${cell} font-mono text-slate-200 sticky left-0 bg-[var(--bg-panel,#0b1220)]`}>{r.name}</td>
      <td className={cell}>{r.group || "—"}</td>
      <td className={cell} data-testid={`funds-audit-class-${r.name}`}>{r.class}</td>
      <td className={cell} data-testid={`funds-audit-present-${r.name}`}>{r.present ? "yes" : "no"}</td>
      <td className={cell}>{r.type || "—"}</td>
      <td className={cell}>{r.present ? (r.numeric_valid ? "valid" : "not numeric") : "—"}</td>
      <td className={cell}>
        <select data-testid={`funds-audit-status-${r.name}`} value={v.status} onChange={set("status")} className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded text-[11px]">
          <option value="unknown">UNKNOWN</option><option value="verified">VERIFIED</option>
        </select>
      </td>
      <td className={cell}><input type="checkbox" data-testid={`funds-audit-display-${r.name}`} checked={!!v.display} onChange={set("display")} /></td>
      <td className={cell}><input type="checkbox" data-testid={`funds-audit-financial-${r.name}`} checked={!!v.financial_use} onChange={set("financial_use")} /></td>
      <td className={cell}><Input data-testid={`funds-audit-meaning-${r.name}`} value={v.meaning || ""} maxLength={120} onChange={set("meaning")} className="h-6 min-w-[140px] text-[11px] bg-[var(--bg-canvas)] border-[var(--border-subtle)]" /></td>
      <td className={cell}><Input data-testid={`funds-audit-evidence-${r.name}`} value={v.evidence || ""} maxLength={300} onChange={set("evidence")} className="h-6 min-w-[160px] text-[11px] bg-[var(--bg-canvas)] border-[var(--border-subtle)]" /></td>
    </tr>
  );
}

function AuditTable({ audit, setAudit }) {
  const [edits, setEdits] = useState({});
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const token = await stepUp(code);
      const fields = Object.fromEntries(Object.entries(edits).map(([n, e]) => {
        const r = audit.rows.find((x) => x.name === n);
        return [n, { status: r.status, display: r.display, financial_use: r.financial_use, meaning: r.meaning, evidence: r.evidence, ...e }];
      }));
      const { data } = await api.post("/brokers/sharekhan/funds-audit/mapping", { step_up_token: token, base_version: audit.mapping_version, fields, confirm_financial: confirm });
      setAudit({ ...audit, mapping_version: data.version, rows: audit.rows.map((r) => ({ ...r, ...(data.fields[r.name] || {}) })) });
      setEdits({});
      toast.success(`Funds field mapping v${data.version} saved`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCode(""); setBusy(false); }
  };
  const s = audit.snapshot;
  return (
    <div className="space-y-2">
      <div data-testid="funds-audit-summary" className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
        <span>Snapshot: {s ? `${s.run_id} · ${utc(s.captured_at)}` : "none"}</span>
        <span>Field spec: {s?.field_spec || "—"}</span>
        <span>Broker timestamp supplied: {s ? (s.broker_timestamp_supplied ? "yes" : "no") : "—"}</span>
        <span data-testid="funds-audit-ingestion">Data received: {audit.ingestion || "—"}</span>
        <span data-testid="funds-audit-completeness">Financial completeness: {audit.financial_completeness.state} ({audit.financial_completeness.verified_fields}/{audit.financial_completeness.observed_fields} fields verified)</span>
        <span data-testid="funds-audit-mapping-version">Mapping v{audit.mapping_version}</span>
        <span data-testid="funds-audit-historical">Older funds snapshots holding an identifier: {audit.historical.with_identifiers} of {audit.historical.funds_snapshots}</span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="funds-audit-table" className="w-full text-[11px] text-slate-400">
          <thead><tr className="text-left text-slate-500">{["Field", "Group", "Class", "Present", "Type", "Numeric", "Status", "Display", "Financial", "Meaning", "Evidence"].map((h, i) => <th key={h} className={`px-2 py-1 font-medium ${i === 0 ? "sticky left-0 bg-[var(--bg-panel,#0b1220)]" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>{audit.rows.map((r) => <AuditRow key={r.name} r={r} edit={edits[r.name]} onEdit={(n, e) => setEdits({ ...edits, [n]: e })} />)}</tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-slate-400"><input type="checkbox" data-testid="funds-audit-confirm-financial" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /> I confirm the evidence for any financial use</label>
        <CodeInput value={code} onChange={setCode} testId="funds-audit-save-code" />
        <Button size="sm" data-testid="funds-audit-save" disabled={!Object.keys(edits).length || code.length !== 6 || busy} onClick={save} className="h-7 bg-blue-600 hover:bg-blue-500 text-white">Save new mapping version</Button>
      </div>
      <div className="text-[10px] text-slate-500">No amounts or account identifiers are shown. Only VERIFIED fields with a meaning and evidence can be displayed or used. Each save needs a new authenticator code (wait for the next code after opening).</div>
    </div>
  );
}

export function FundsAuditSection() {
  const [code, setCode] = useState("");
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const token = await stepUp(code);
      setAudit((await api.post("/brokers/sharekhan/funds-audit/view", { step_up_token: token })).data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCode(""); setBusy(false); }
  };
  return (
    <div data-testid="funds-audit-section" className="rounded-md border border-[var(--border-subtle)] p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-200"><ClipboardCheck className="w-3.5 h-3.5" /> Funds field audit (owner-only, no amounts)</div>
        {audit ? (
          <button data-testid="funds-audit-close" className="text-xs text-slate-300 underline underline-offset-2 hover:text-white" onClick={() => setAudit(null)}>Close</button>
        ) : (
          <div className="flex items-center gap-2">
            <CodeInput value={code} onChange={setCode} testId="funds-audit-code" />
            <Button size="sm" data-testid="funds-audit-open" disabled={code.length !== 6 || busy} onClick={open} className="h-7 bg-slate-700 hover:bg-slate-600 text-white"><Lock className="w-3.5 h-3.5 mr-1" />Open audit</Button>
          </div>
        )}
      </div>
      {audit && <AuditTable audit={audit} setAudit={setAudit} />}
    </div>
  );
}
